// The Move & Schedule panel — ZT's ScheduleConflictPanel, reimplemented
// (round 22).
//
// Grouped PER RESOURCE, and the tick is per resource too: ticking says "free
// this resource from everything it clashes with, then book it here". A
// resource is tickable only when EVERY conflict on it is a booking — being
// away or retired is not releasable from this panel, so those rows say why
// and the resource is left out of the save entirely. Each ticked clash shows
// its outcome line straight from the server's `ifReleased` plan: what the
// panel shows is what happens, because both were computed under the same
// lock.
import { dateRangeLabel, formatClockRange } from '../../lib/dates';
import type { Conflict } from '../../schedulingApi';

export interface ResourceConflicts {
  resourceId: string;
  resourceName: string;
  conflicts: Conflict[];
}

/** A resource is releasable only when everything in its way is a booking. */
export function isReleasable(entry: ResourceConflicts): boolean {
  return entry.conflicts.every((c) => c.type === 'booking');
}

function planLine(conflict: Conflict & { type: 'booking' }): string {
  const plan = conflict.ifReleased;
  switch (plan.action) {
    case 'none':
      return '';
    case 'delete':
      return '→ booking removed entirely';
    case 'trim':
      return `→ becomes ${dateRangeLabel(plan.startDate, plan.endDate)}`;
    case 'split':
      return `→ becomes ${dateRangeLabel(plan.startDate, plan.endDate)} and ${dateRangeLabel(
        plan.tailStartDate,
        plan.tailEndDate
      )}`;
  }
}

export function ScheduleConflictPanel({
  entries,
  ticked,
  onToggle,
  confirmLabel,
  onConfirm,
  busy,
}: {
  entries: ResourceConflicts[];
  ticked: Set<string>;
  onToggle: (resourceId: string) => void;
  confirmLabel: string;
  onConfirm: () => void;
  busy: boolean;
}) {
  const anyTickedRewritesPast = entries.some(
    (entry) =>
      ticked.has(entry.resourceId) &&
      entry.conflicts.some(
        (c) => c.type === 'booking' && c.ifReleased.action !== 'none' && c.ifReleased.rewritesPast
      )
  );

  return (
    <div
      className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
      role="alert"
    >
      <p className="font-semibold">Some of those resources are already committed.</p>
      <p className="mt-0.5 text-xs">
        Tick a resource to free it from what is in the way and schedule it here. Unticked
        resources are left out entirely; their bookings stay put.
      </p>

      <ul className="mt-2 space-y-2">
        {entries.map((entry) => {
          const releasable = isReleasable(entry);
          const isTicked = ticked.has(entry.resourceId);
          return (
            <li
              key={entry.resourceId}
              className="rounded border border-amber-200 bg-white/60 px-2 py-1.5"
            >
              <label className="flex items-start gap-2">
                {releasable ? (
                  <input
                    type="checkbox"
                    checked={isTicked}
                    onChange={() => onToggle(entry.resourceId)}
                    className="mt-0.5 h-5 w-5 flex-none rounded border-gray-300 text-brand-600 focus:ring-brand-500 sm:h-4 sm:w-4"
                  />
                ) : (
                  <span className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">{entry.resourceName}</span>
                  {!releasable && (
                    <span className="ml-1.5 text-xs">
                      cannot be freed from here, so it is left out of this booking.
                    </span>
                  )}
                  <ul className="mt-1 space-y-1">
                    {entry.conflicts.map((conflict, index) => {
                      if (conflict.type === 'booking') {
                        return (
                          <li key={index} className="text-xs">
                            <span className="font-medium">
                              {conflict.kind === 'maintenance'
                                ? `Maintenance with ${conflict.supplierName ?? 'a supplier'}, all day`
                                : `${conflict.jobNumber} · ${conflict.jobTitle}`}
                            </span>
                            {', '}
                            {dateRangeLabel(conflict.startDate, conflict.endDate)}
                            {conflict.startTime
                              ? `, ${formatClockRange(conflict.startTime, conflict.endTime)}`
                              : ''}
                            {isTicked && (
                              <span className="ml-1 font-medium text-amber-900">
                                {planLine(conflict)}
                              </span>
                            )}
                            {isTicked &&
                              conflict.ifReleased.action !== 'none' &&
                              conflict.ifReleased.rewritesPast && (
                              <span className="block text-red-700">
                                ⚠ Includes days already worked; the record will change.
                              </span>
                            )}
                            {isTicked && conflict.releasingLosesHours && (
                              <span className="block text-amber-900">
                                ⚠ Whole days are freed, including hours outside the new
                                booking&apos;s times.
                              </span>
                            )}
                          </li>
                        );
                      }
                      if (conflict.type === 'unavailability') {
                        return (
                          <li key={index} className="text-xs">
                            Away: {conflict.reason},{' '}
                            {dateRangeLabel(conflict.startDate, conflict.endDate)}
                          </li>
                        );
                      }
                      return (
                        <li key={index} className="text-xs">
                          {conflict.resourceName} is {conflict.status}; only an available
                          resource can be booked.
                        </li>
                      );
                    })}
                  </ul>
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {anyTickedRewritesPast && (
        <p className="mt-2 text-xs font-medium text-red-700">
          ⚠ Going ahead rewrites days already worked. Only do this when the record is wrong.
        </p>
      )}

      <div className="mt-2">
        <button
          type="button"
          className="btn-warning btn-sm"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Saving…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}
